// @flow

import React, { Component } from 'react';
import { Alert, Button, ButtonGroup, Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { downloadAuthenticated } from '../../store/requests';

type WindowDays = 1 | 7 | 30;

type PropsType = {
    /** Compact card for ops overview; omit for full-width panel */
    compact?: boolean,
};

type StateType = {
    windowDays: WindowDays,
    provider: string,
    exporting: boolean,
    error: ?string,
    lastFileName: ?string,
};

const WINDOW_OPTIONS: Array<{ days: WindowDays, label: string }> = [
    { days: 1, label: 'Today' },
    { days: 7, label: '7 days' },
    { days: 30, label: '30 days' },
];

/**
 * Downloadable payment ledger for CS / ops reconciliation.
 * Replaces the Support Hub one-click "CSV (7d)" with a clearer period + provider control.
 */
class PaymentLedgerExport extends Component<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            windowDays: 7,
            provider: 'stripe',
            exporting: false,
            error: null,
            lastFileName: null,
        };
    }

    download = () => {
        const { windowDays, provider } = this.state;
        const until = Math.floor(Date.now() / 1000);
        const since = until - (windowDays * 24 * 60 * 60);
        const providerQuery = provider ? `&provider=${encodeURIComponent(provider)}` : '';
        const fileName = `billmaster-payment-ledger-${windowDays}d-${since}.csv`;

        this.setState({ exporting: true, error: null, lastFileName: null });
        downloadAuthenticated(
            `reports/payments?from=${since}&to=${until}${providerQuery}`,
            fileName,
        )
            .then(() => this.setState({ exporting: false, lastFileName: fileName }))
            .catch((error) => {
                this.setState({
                    exporting: false,
                    error: (error && error.message) || 'Payment ledger download failed',
                });
            });
    };

    render() {
        const { compact } = this.props;
        const { windowDays, provider, exporting, error, lastFileName } = this.state;

        return (
            <Card
                className={`dashboard-panel${compact ? '' : ' bottom-spaced-element'}`}
                data-testid="payment-ledger-export"
                style={compact ? { border: '1px solid #dbe4ea', height: '100%' } : undefined}
            >
                <CardBody>
                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                        <div>
                            <div className="text-uppercase mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.12em', color: '#64748b' }}>
                                Finance ops
                            </div>
                            <h2 className="h5 mb-1">Payment ledger</h2>
                            <p className="mb-0 text-muted" style={{ maxWidth: '36rem' }}>
                                Download provider payment events for reconciliation (captures, refunds, tips).
                                Spreadsheet format for accounting � not a support triage tool.
                            </p>
                        </div>
                    </div>

                    <Row className="align-items-end mt-3">
                        <Col md="5" className="mb-2">
                            <Label className="small text-muted text-uppercase" style={{ letterSpacing: '0.08em' }}>
                                Period
                            </Label>
                            <div>
                                <ButtonGroup className="d-flex flex-wrap">
                                    {WINDOW_OPTIONS.map((option) => (
                                        <Button
                                            key={option.days}
                                            color={windowDays === option.days ? 'primary' : 'secondary'}
                                            outline={windowDays !== option.days}
                                            size="sm"
                                            onClick={() => this.setState({ windowDays: option.days })}
                                            data-testid={`payment-ledger-window-${option.days}`}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </div>
                        </Col>
                        <Col md="3" className="mb-2">
                            <FormGroup className="mb-0">
                                <Label for="payment-ledger-provider" className="small text-muted text-uppercase" style={{ letterSpacing: '0.08em' }}>
                                    Provider
                                </Label>
                                <Input
                                    id="payment-ledger-provider"
                                    type="select"
                                    bsSize="sm"
                                    value={provider}
                                    onChange={(event) => this.setState({ provider: event.target.value })}
                                >
                                    <option value="stripe">Stripe</option>
                                    <option value="">All providers</option>
                                    <option value="payfacto">PayFacto</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="4" className="mb-2 text-md-right">
                            <Button
                                color="primary"
                                disabled={exporting}
                                onClick={this.download}
                                data-testid="payment-ledger-download"
                            >
                                {exporting ? 'Preparing�' : 'Download ledger'}
                            </Button>
                        </Col>
                    </Row>

                    {error ? <Alert color="danger" className="mt-3 mb-0">{error}</Alert> : null}
                    {lastFileName && !error ? (
                        <Alert color="success" className="mt-3 mb-0">
                            Downloaded <code>{lastFileName}</code>
                        </Alert>
                    ) : null}
                </CardBody>
            </Card>
        );
    }
}

export default PaymentLedgerExport;
